import { useHubFrame } from '../../theaWeb/hub/HubFrameContext';
import React from 'react';
import styled, { css } from 'styled-components';
import { HeaderAccountMenu } from '../../theaWeb/auth/HeaderAccountMenu';
import { PAGE_GUTTER_DESKTOP } from '../../theaWeb/lib/pageGutter';
import {
  SiteNavLinks,
  SiteWordmark,
  type SiteHeaderNavItem,
} from './SiteNavLinks';

// Re-exported for existing importers; the type now lives with the shared
// nav primitives (SiteNavLinks) so BoardHeader can use it too.
export type { SiteHeaderNavItem };

export interface SiteHeaderProps {
  /**
   * Right-side actions slot. Defaults to <HeaderAccountMenu />, which renders
   * the Sign-in pill for anonymous users and an avatar + Log out dropdown for
   * permanent users. Pass `null` to render nothing.
   */
  actions?: React.ReactNode;
  /**
   * Legacy prop kept for backward compatibility with callers that haven't
   * migrated to AuthGateProvider yet. Ignored when `actions` is provided and
   * has no effect on the default <HeaderAccountMenu />, which uses the
   * AuthGateContext directly.
   */
  onSignInClick?: () => void;
  /** Click handler for the wordmark. SiteHeader internally calls
   *  `preventDefault()` before invoking this — callers don't need to handle
   *  the click event. The wordmark is an <a href={logoHref}>, and without
   *  preventDefault the browser would navigate immediately and bypass any
   *  `requestLeave`-style confirmation modal the handler opens. Centralizing
   *  preventDefault here means every caller is safe by default (bug #62). */
  onLogoClick?: () => void;
  /** href for the wordmark link. Defaults to "/". */
  logoHref?: string;
  /** When true, render the header transparently with white wordmark — for
   *  layouts where the header floats absolutely over a hero image. The
   *  consumer is responsible for positioning the header over the image
   *  (e.g. wrap in a relative parent and absolutely-position SiteHeader). */
  overHero?: boolean;
  /** Right-grouped text-link nav items (e.g. People / Calendar). Rendered
   *  between the wordmark and the actions slot, hidden at <1024px so mobile
   *  uses the bottom tab bar. Pattern reference: Etsy / Pinterest's
   *  right-grouped secondary nav. Pass `undefined` (default) to skip. */
  navItems?: SiteHeaderNavItem[];
  /** Keep the right-side actions (Sign-in / avatar) visible on mobile.
   *  Surfaces with a bottom tab bar hide them by default; the first-time
   *  homepage (no bottom bar) opts in so Sign-in shows top-right on mobile
   *  too (Kate 2026-06-04).
   *
   *  Pass a BOOLEAN EXPRESSION, not a bare `true`, on any surface whose bottom
   *  bar is conditional. The promoted `/liked/:recipientId` board (THE-705 / WS4)
   *  mounts `MobileTabBarMount`, which renders nothing until
   *  `uid !== null && (isPermanent || friends.length > 0)` — so for a signed-out
   *  visitor with no recipients on this device there is no You tab to own
   *  account, and the rule below would leave the surface with no sign-in door at
   *  all. That page passes `showActionsOnMobile={!tabBarVisible && !resolving}`,
   *  read from `theaWeb/calendar/mobileTabBarVisibility.ts` — the same predicate
   *  the bar renders on. The default stays `false`: this is a fallback for the
   *  gap, not a second rule. */
  showActionsOnMobile?: boolean;
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to
   * `false` so every existing story/test and real visitor keeps rendering
   * today's production layout when the hub gate is off. Each caller (LandingPage,
   * GiftGuidesPage, CalendarPage, ...) resolves the flag once and passes it
   * down.
   */
  wideGutterEnabled?: boolean;
}

const Header = styled.header<{ $overHero?: boolean }>`
  position: relative;
  width: 100%;
  padding: 16px 0;
  ${({ $overHero }) =>
    $overHero
      ? css`background: transparent;`
      : css`background: hsl(var(--background));`}
`;

// THE-1161: the >=1024px gutter matches PAGE_GUTTER_DESKTOP, which is also
// what every page's own content reads — so the wordmark and nav links keep
// lining up with the page under them past 1568px wide, not just below it.
// `$wideGutter`: off/omitted reproduces the pre-PR flat 64px until Kate
// reviews this at a preview link. It arrives here as a plain prop each
// caller derives from its own `useGiftGuideHubGate()` read (Kate,
// 2026-10-28, PR #883: no switch of its own) — a DIFFERENT input from this
// component's own `hubFrame` early-return below, which asks a different
// question (is the Hub's own chrome active for this route, so this header
// should not render at all) and is read from `HubFrameContext` directly.
// `hubFrame` being false (the production default) does not, on its own,
// keep this padding at its pre-PR value.
const Inner = styled.div<{ $wideGutter?: boolean }>`
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media (min-width: 640px) {
    padding: 0 32px;
  }
  @media (min-width: 1024px) {
    padding: 0 ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
  }
`;

const RightCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  /* 32px breathing room between the nav items and the avatar so they
     don't read as a single tight group. */
`;

const Actions = styled.div<{ $showOnMobile?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  /* Hide the top-right account slot (the avatar / Sign-in pill) on mobile.
   * The bottom tab bar's "You" tab owns account + notifications there, so
   * a top-right avatar would be a duplicate entry point. Desktop has no
   * bottom bar, so the avatar stays at ≥1024. (The detail boards surface
   * keeps its own mobile affordance via BoardHeaderActions — it doesn't
   * use SiteHeader.) Surfaces with no bottom bar (the first-time homepage)
   * opt in via $showOnMobile so Sign-in shows top-right on mobile too.
   *
   * "Has a bottom tab bar" is a RUNTIME question on surfaces whose bar is
   * conditional, and there the opt-in is per-render rather than per-surface: the
   * promoted /liked/:recipientId board hides these actions while its bar is up
   * and shows them when it isn't, so the rule holds in the normal case and the
   * door survives the state where the bar is absent. See the prop docs. */
  @media (max-width: 1023px) {
    display: ${({ $showOnMobile }) => ($showOnMobile ? 'flex' : 'none')};
  }
`;

export const SiteHeader: React.FC<SiteHeaderProps> = ({
  actions,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for API compatibility; see prop docs above.
  onSignInClick,
  onLogoClick,
  logoHref = '/',
  overHero = false,
  navItems,
  showActionsOnMobile = false,
  wideGutterEnabled = false,
}) => {
  // Default actions: account menu only. The red-dot + alerts list both
  // live inside HeaderAccountMenu now (per Kate's feedback 2026-05-12 —
  // single avatar with a red dot, not a separate bell icon).
  const renderedActions =
    actions === undefined ? <HeaderAccountMenu overHero={overHero} /> : actions;

  // Wrap so the anchor's default navigation doesn't race ahead of the
  // handler — see prop docs. When no handler is provided, fall through to
  // the natural <a href={logoHref}> behavior.
  const handleClick = onLogoClick
    ? (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onLogoClick();
      }
    : undefined;

  const hubFrame = useHubFrame();
  if (hubFrame) return null;

  return (
    <Header $overHero={overHero}>
      <Inner $wideGutter={wideGutterEnabled}>
        <SiteWordmark href={logoHref} onClick={handleClick} $overHero={overHero}>
          thea
        </SiteWordmark>
        <RightCluster>
          {navItems && <SiteNavLinks items={navItems} overHero={overHero} />}
          <Actions $showOnMobile={showActionsOnMobile}>{renderedActions}</Actions>
        </RightCluster>
      </Inner>
    </Header>
  );
};
